import React, { useState, useEffect } from 'react';
import {
  Box, 
  Typography, 
  Button, 
  Modal, 
  List, 
  ListItem, 
  ListItemContent, 
  Divider
} from '@mui/joy';
import EvaluationForm, { deserializeEvaluation, createEmptyEvaluation } from './EvaluationForm';

const CustomEvaluationsManager = ({ services }) => {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEvaluation, setCurrentEvaluation] = useState(createEmptyEvaluation());
  const [isEditing, setIsEditing] = useState(false);
  
  // Load custom evaluations when the component mounts
  useEffect(() => {
    loadCustomEvaluations();
  }, []);

  // Load custom evaluations
  const loadCustomEvaluations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/custom-evaluations', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEvaluations(data);
      } else if (response.status === 401) {
        setError('Sie sind nicht angemeldet. Bitte melden Sie sich an.');
      } else {
        setError(`Fehler beim Laden: ${response.statusText}`);
      }
    } catch (err) {
      setError('Fehler beim Laden der benutzerdefinierten Auswertungen');
      console.error('Error loading custom evaluations:', err);
    } finally {
      setLoading(false);
    }
  };

  // Save a custom evaluation
  const saveCustomEvaluation = async (name, content, id = null) => {
    try {
      if (id) {
        // Update existing evaluation
        const response = await fetch(`/api/custom-evaluations/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ name, content }),
        });

        if (response.ok) {
          alert(`Auswertung "${name}" wurde aktualisiert.`);
          await loadCustomEvaluations();
          setIsModalOpen(false);
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
          alert(`Fehler beim Aktualisieren: ${errorData.error || response.statusText}`);
        }
      } else {
        // Create new evaluation
        const response = await fetch('/api/custom-evaluations', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ name, content }),
        });

        if (response.ok) {
          alert(`Auswertung "${name}" wurde gespeichert.`);
          await loadCustomEvaluations();
          setIsModalOpen(false);
        } else {
          const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
          alert(`Fehler beim Speichern: ${errorData.error || response.statusText}`);
        }
      }
    } catch (error) {
      console.error('Error saving custom evaluation:', error);
      alert('Fehler beim Speichern der Auswertung');
    }
  };

  // Delete a custom evaluation
  const deleteCustomEvaluation = async (id) => {
    try {
      const response = await fetch(`/api/custom-evaluations/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        alert('Auswertung wurde gelöscht.');
        await loadCustomEvaluations();
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unbekannter Fehler' }));
        alert(`Fehler beim Löschen: ${errorData.error || response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting custom evaluation:', error);
      alert('Fehler beim Löschen der Auswertung');
    }
  };

  // Handle evaluation create/edit save
  const handleSave = (name, content) => {
    if (isEditing && currentEvaluation.id) {
      saveCustomEvaluation(name, content, currentEvaluation.id);
    } else {
      saveCustomEvaluation(name, content);
    }
  };

  // Handle edit button click
  const handleEditEvaluation = (evaluation) => {
    const deserializedEval = deserializeEvaluation(evaluation.content);
    if (deserializedEval) {
      setCurrentEvaluation({
        ...deserializedEval,
        id: evaluation.id
      });
      setIsEditing(true);
      setIsModalOpen(true);
    } else {
      alert('Fehler beim Laden der Auswertung');
    }
  };

  // Handle create new button click
  const handleCreateNew = () => {
    setCurrentEvaluation(createEmptyEvaluation());
    setIsEditing(false);
    setIsModalOpen(true);
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography level="h2">Benutzerdefinierte Auswertungen</Typography>
        <Button 
          variant="solid" 
          color="primary" 
          onClick={handleCreateNew}
        >
          Neue Auswertung erstellen
        </Button>
      </Box>

      {error && (
        <Typography color="danger" sx={{ my: 2 }}>
          {error}
        </Typography>
      )}

      {loading ? (
        <Typography>Lade Auswertungen...</Typography>
      ) : evaluations.length === 0 ? (
        <Typography>Keine benutzerdefinierten Auswertungen vorhanden.</Typography>
      ) : (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 'sm' }}>
          {evaluations.map((evaluation) => (
            <React.Fragment key={evaluation.id}>
              <ListItem
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 2
                }}
              >
                <ListItemContent>
                  <Typography fontWeight="bold">{evaluation.name}</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Zuletzt aktualisiert: {new Date(evaluation.updated_at).toLocaleString()}
                  </Typography>
                </ListItemContent>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleEditEvaluation(evaluation)}
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="danger"
                    onClick={() => {
                      if (window.confirm(`Möchten Sie die Auswertung "${evaluation.name}" wirklich löschen?`)) {
                        deleteCustomEvaluation(evaluation.id);
                      }
                    }}
                  >
                    Löschen
                  </Button>
                </Box>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>
      )}

      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: 'background.paper',
            borderRadius: 'md',
            p: 3,
            boxShadow: 'lg'
          }}
        >
          <Typography level="h3" sx={{ mb: 2 }}>
            {isEditing ? 'Auswertung bearbeiten' : 'Neue Auswertung erstellen'}
          </Typography>
          
          <EvaluationForm
            evaluation={currentEvaluation}
            onEvaluationChange={setCurrentEvaluation}
            services={services}
            showAdvancedView={true}
            onSave={handleSave}
            submitButtonText={isEditing ? "Aktualisieren" : "Speichern"}
          />
          
          <Button
            variant="outlined"
            color="neutral"
            onClick={() => setIsModalOpen(false)}
            sx={{ mt: 2 }}
          >
            Abbrechen
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default CustomEvaluationsManager;
